import React from 'react';
import { InView } from 'react-intersection-observer';

import { Box, Popover } from '@mui/material';

import styled from '@emotion/styled';
import dayjs from 'dayjs';

import { ChatMessageItem, ConversationItem } from '../../../graphql/API';
import icons from '../../../themes/icons';
import { EditMessageDialog } from '../../Dialogs/EditMessageDialog/EditMessageDialog';
import { Image } from '../Image/Image';
import { Ticket } from '../Ticket/Ticket';
import { LinkifiedText } from './LinkifiedText';
import './MessageV2.css';

type Props = {
  index: number;
  userId: string;
  msg: ChatMessageItem;
  messages: Nullable<ChatMessageItem | undefined>[];
  conversation: ConversationItem;
  handleMessageUpdate?: (message: ChatMessageItem) => void;
  lastElementRef: (node: HTMLDivElement | null) => void;
};

export const MessageV2: React.FC<Props> = ({
  index,
  userId,
  msg,
  messages,
  conversation,
  handleMessageUpdate,
  lastElementRef,
}) => {
  const messageContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [messagePopup, setMessagePopup] = React.useState<HTMLDivElement | null>(null);
  const [messageEditPopup, setMessageEditPopup] = React.useState(false);

  const handleMessagePopup = () => {
    if (messageContainerRef.current) setMessagePopup(messageContainerRef.current);
  };

  const handleMessageEdit = () => {
    setMessageEditPopup((prev) => !prev);
    setMessagePopup(null);
  };

  const handleClose = () => {
    setMessagePopup(null);
  };

  const open = Boolean(messagePopup);
  const id = open ? 'message-popover' : undefined;

  const isMediaMessage =
    msg?.messageType === 'photo' ||
    msg?.messageType === 'video' ||
    msg?.messageType === 'link' ||
    msg?.message === 'image';

  return (
    <div
      {...(messages.length === index + 1 && {
        ref: lastElementRef,
      })}
      className={
        userId === msg?.fromUserId
          ? 'message-content-sender'
          : msg?.messageType === 'endchat'
            ? 'message-content-receiver-endchat'
            : 'message-content-receiver'
      }
      key={`${index}-${msg?.messageTime}-${conversation?.conversationId}`}
    >
      <InView
        as='div'
        style={{ width: '100%' }}
        onChange={(inView) => {
          if (inView && msg && msg.toUserId === userId && !msg.seen && handleMessageUpdate) {
            handleMessageUpdate(msg);
          }
        }}
      >
        <Box
          width='100%'
          marginBottom='4px'
          display='flex'
          flexDirection='column'
          alignItems={userId === msg?.fromUserId ? 'flex-end' : 'flex-start'}
        >
          {msg?.messageType === 'photo' ||
          msg?.messageType === 'video' ||
          msg?.message === 'image' ? (
            <Box sx={{ width: 'min(75%, 460px)' }}>
              <Image
                src={msg?.message || ''}
                alt={msg.messageTime}
                type={msg.messageType || ''}
                rejected={msg?.rejected || false}
                imageHeight='300px'
              />
            </Box>
          ) : msg?.messageType === 'link' ? (
            <Ticket message={msg} />
          ) : msg?.messageType === 'endchat' ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                marginTop: '32px',
                marginBottom: '35px',
              }}
            >
              <LineCloseChat />
              <ChatClosedMessage>{msg?.message}</ChatClosedMessage>
              <LineCloseChat />
            </Box>
          ) : (
            <div
              className={`${
                userId === msg?.fromUserId ? 'message-sender-v2' : 'message-receiver-v2'
              } message-container-1`}
              ref={messageContainerRef}
            >
              {userId === msg?.fromUserId && (
                <button
                  aria-describedby={id}
                  className={'arrow-container'}
                  onClick={handleMessagePopup}
                >
                  <icons.ExpandMoreIcon style={{ color: 'rgba(92, 94, 149, 1)' }} />
                </button>
              )}
              <span className='message-text'>
                <LinkifiedText text={msg?.message} />
              </span>
              <span className='message-meta-inline'>
                {msg?.messageTime ? dayjs(msg.messageTime).format('LT') : null}
                {msg.toUserId !== userId && msg?.seen && (
                  <icons.DoneAllIcon htmlColor='#5C5E95' fontSize='inherit' />
                )}
              </span>
            </div>
          )}
          {isMediaMessage && (
            <Box
              display={'flex'}
              alignItems={'center'}
              marginTop={'2px'}
              justifyContent={userId === msg?.fromUserId ? 'flex-end' : 'flex-start'}
            >
              <span className='time-text'>
                {msg?.messageTime ? dayjs(msg.messageTime).format('LT') : null}
              </span>
              {msg.toUserId !== userId && msg?.seen && (
                <Box component='span' sx={{ marginLeft: '5px' }}>
                  <icons.DoneAllIcon htmlColor='#5C5E95' fontSize='inherit' />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </InView>

      <EditMessageDialog open={messageEditPopup} onClose={handleMessageEdit} messageItem={msg} />
      <Popover
        id={id}
        open={open}
        anchorEl={messagePopup}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{ borderRadius: '10px' }}
      >
        <Box
          display={'flex'}
          flexDirection={'column'}
          padding={'15px'}
          style={{ gap: '10px', minWidth: '240px' }}
        >
          <Box
            component={'button'}
            display={'flex'}
            alignItems={'center'}
            style={{
              gap: '12px',
              paddingBottom: '14px',
              border: 'none',
              outline: 'none',
              borderBottom: '1px solid #F3F3F3',
              background: 'transparent',
              cursor: 'pointer',
            }}
            onClick={handleMessageEdit}
          >
            <icons.EditIcon />
            <PopoverItemText>Edit message</PopoverItemText>
          </Box>
        </Box>
      </Popover>
    </div>
  );
};

const ChatClosedMessage = styled.span`
  font-size: 10px;
  color: #949ba5;
  font-weight: 600;
  text-transform: uppercase;
  min-width: 32px;
  display: inline-block;
`;

const LineCloseChat = styled.div`
  height: 1px;
  background: #949ba5;
  width: 100%;
`;

const PopoverItemText = styled.p<{ color?: string }>`
  color: ${(props) => props.color || '#111'};
  font-family: Poppins;
  font-size: 15px;
  font-weight: 400;
  margin: 0;
`;
